import { motion, useAnimation, Variants } from "framer-motion";
import {
  MutableRefObject,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { ImageConfig, SceneItemOffsetPosition } from "../../models/client";
import { Position } from "../../models/client/position";
import { Colors } from "../../models/theme";
import { easings } from "../../styles/utils";
import { CircularMotion } from "../CircularMotion";
import { SceneMarkerImage } from "./SceneMarkerImage";

export interface SceneMarkerProps {
  id: number | string;
  position: Position;
  color: keyof Colors;
  image: ImageConfig;
  altImgPos?: boolean;
  size: number;
  offsetPosition: SceneItemOffsetPosition;
}

const HOTSPOT_SIZE = 200;

const Outer = styled(motion.div)<{ position: Position }>`
  position: absolute;
  top: ${({ position }) => position.y}%;
  left: ${({ position }) => position.x}%;
  transform: translate(-50%, -50%);
  // background-color: rgba(255, 255, 255, 0.2);
  height: ${HOTSPOT_SIZE}px;
  width: ${HOTSPOT_SIZE}px;
  border-radius: 50%;
`;

const Marker = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  box-shadow: 5px 5px 10px 0 rgba(0, 0, 0, 0.31);
  transform: translate(-50%, -50%);
`;

const OuterCircle = styled.circle`
  fill: white;
  opacity: 0.5;
`;

const InnerCircle = styled.circle<{ color: keyof Colors }>`
  fill: ${({ theme, color }) => theme.colors[color]};
`;

const Svg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  /* transform: translate(-50%, -50%); */
  pointer-events: none;
`;

const Motion = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  pointer-events: none;
`;

const Center = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
`;

const Offset = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
`;

const markerVariants: Variants = {
  expanded: { scale: 1.4, x: "-50%", y: "-50%" },
  default: { scale: 1, x: "-50%", y: "-50%" },
};

export const SceneMarker: React.FC<SceneMarkerProps> = (props) => {
  const magnet: MutableRefObject<any> = useRef<HTMLElement>();

  const [expanded, setExpanded] = useState(false);

  const controls = useAnimation();

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!expanded) {
        setExpanded(true);
      }

      const markerRect = magnet.current.getBoundingClientRect();

      // distance from cursor to centre
      const diffX = e.clientX - markerRect.x;
      const diffY = e.clientY - markerRect.y;

      controls.start({
        x: diffX,
        y: diffY,
        transition: {
          type: "spring",
          damping: 30,
          stiffness: 200,
        },
      });
    },
    [controls, expanded]
  );

  const onOuterMouseLeave = (e: SyntheticEvent) => {
    controls.start({
      x: 0,
      y: 0,
      transition: {
        type: "spring",
        damping: 10,
        stiffness: 60,
      },
    });
    setExpanded(false);
  };

  return (
    <Outer
      position={props.position}
      onMouseMove={onMouseMove}
      onMouseLeave={onOuterMouseLeave}
      whileHover="hover"
      id="o"
    >
      <Offset>
        <SceneMarkerImage
          imgSrc={props.image.src}
          altPos={props.altImgPos}
          size={props.size}
          position={props.offsetPosition}
        />
        <Center ref={magnet} />

        <Motion animate={controls}>
          <Marker
            initial={false}
            animate={expanded ? "expanded" : "default"}
            variants={markerVariants}
          >
            <Svg width="48" height="48">
              <OuterCircle cx="24" cy="24" r="24"></OuterCircle>
              <InnerCircle
                cx="24"
                cy="24"
                r="16"
                color={props.color}
              ></InnerCircle>
              <path d="M 24 18 L 24 30" strokeWidth="2" stroke="white"></path>
              <path d="M 18 24 L 30 24" strokeWidth="2" stroke="white"></path>
            </Svg>
          </Marker>
        </Motion>
      </Offset>
    </Outer>
  );
};

// transitionDuration: "0.5s",
// transitionTimingFunction: "cubic-bezier(0, 0.55, 0.45, 1)",
